/**
 * Offline Queue Status Component
 * 
 * Shows the status of offline form submissions:
 * - Pending count badge
 * - Online/offline indicator
 * - Sync progress
 * - Queue management dialog
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Cloud, CloudOff, RefreshCw, Trash2, AlertCircle, CheckCircle, 
  Clock, Upload, Loader2, WifiOff 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  getQueueStats, 
  getFullQueue, 
  syncQueue, 
  removeFromQueue,
  retryFailedItems,
  clearSyncedItems,
  isOnline,
  QueueStatus,
  FormType,
  setupAutoSync,
  initOfflineDB
} from '@/services/unifiedOfflineService';
import { formatDistanceToNow } from 'date-fns';

const FormTypeLabels = {
  [FormType.HAZARD_REPORT]: 'Hazard Report',
  [FormType.INCIDENT]: 'Incident Report',
  [FormType.RISK_ASSESSMENT]: 'Risk Assessment',
  [FormType.AUDIT_FINDING]: 'Audit Finding',
  [FormType.CAPA]: 'CAPA',
  [FormType.SURVEY_RESPONSE]: 'Survey Response'
};

const StatusColors = {
  [QueueStatus.PENDING]: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
  [QueueStatus.SYNCING]: 'bg-sky-500/20 text-sky-400 border-sky-500/50',
  [QueueStatus.SYNCED]: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
  [QueueStatus.FAILED]: 'bg-red-500/20 text-red-400 border-red-500/50'
};

const StatusIcons = {
  [QueueStatus.PENDING]: Clock,
  [QueueStatus.SYNCING]: Loader2,
  [QueueStatus.SYNCED]: CheckCircle,
  [QueueStatus.FAILED]: AlertCircle
};

export default function OfflineQueueStatus() {
  const { api } = useAuth();
  const { isDark } = useTheme();
  const [online, setOnline] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending: 0, failed: 0 });
  const [queueItems, setQueueItems] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Initialize and setup listeners
  useEffect(() => {
    const init = async () => {
      try {
        await initOfflineDB();
        if (api) {
          setupAutoSync(api);
        }
        setInitialized(true);
        await refreshStats();
      } catch (error) {
        console.error('Failed to initialize offline queue:', error);
      }
    };

    init();

    // Online/offline listeners
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Queue update listener
    const handleQueueUpdate = () => refreshStats();
    window.addEventListener('offlineQueueUpdated', handleQueueUpdate);
    
    // Sync listeners
    const handleSyncStart = () => setSyncing(true);
    const handleSyncEnd = () => {
      setSyncing(false);
      refreshStats();
    };
    window.addEventListener('offlineSyncStarted', handleSyncStart);
    window.addEventListener('offlineSyncCompleted', handleSyncEnd);

    // Set initial online state
    setOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('offlineQueueUpdated', handleQueueUpdate);
      window.removeEventListener('offlineSyncStarted', handleSyncStart);
      window.removeEventListener('offlineSyncCompleted', handleSyncEnd);
    };
  }, [api]);

  const refreshStats = async () => {
    try {
      const newStats = await getQueueStats();
      setStats(newStats);
      
      if (dialogOpen) {
        const items = await getFullQueue();
        setQueueItems(items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      }
    } catch (error) {
      console.error('Failed to refresh queue stats:', error);
    }
  };

  const handleOpenDialog = async () => {
    setDialogOpen(true);
    const items = await getFullQueue();
    setQueueItems(items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
  };

  const handleSync = async () => {
    if (!api || !online) return;
    setSyncing(true);
    try {
      await syncQueue(api);
    } finally {
      setSyncing(false);
      await refreshStats();
    }
  };

  const handleRetryFailed = async () => {
    await retryFailedItems();
    await refreshStats();
    if (online && api) {
      await handleSync();
    }
  };

  const handleRemoveItem = async (id) => {
    await removeFromQueue(id);
    await refreshStats();
  };

  const handleClearSynced = async () => {
    await clearSyncedItems();
    await refreshStats();
  };

  // Don't render if not initialized
  if (!initialized) return null;

  const hasPending = stats.pending > 0;
  const hasFailed = stats.failed > 0;

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleOpenDialog}
          className={cn(
            "relative flex items-center gap-2 px-3 py-2 rounded-lg transition-colors",
            !online && "animate-pulse",
            isDark 
              ? "text-slate-400 hover:text-white hover:bg-slate-800" 
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          )}
          data-testid="offline-queue-btn"
        >
          {online ? (
            <Cloud className={cn("w-4 h-4", syncing && "animate-pulse text-sky-400")} />
          ) : (
            <CloudOff className="w-4 h-4 text-amber-400" />
          )}
          
          {/* Pending badge */}
          {hasPending && (
            <Badge 
              variant="secondary" 
              className="bg-amber-500/20 text-amber-400 border-amber-500/50 text-xs px-1.5 py-0"
            >
              {stats.pending}
            </Badge>
          )}
          
          {/* Failed badge */}
          {hasFailed && (
            <Badge 
              variant="secondary" 
              className="bg-red-500/20 text-red-400 border-red-500/50 text-xs px-1.5 py-0"
            >
              {stats.failed}!
            </Badge>
          )}
          
          {/* Syncing indicator */}
          {syncing && (
            <Loader2 className="w-3 h-3 animate-spin text-sky-400" />
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className={cn(
        "max-w-lg",
        isDark ? "bg-slate-900 border-slate-800" : "bg-white border-gray-200"
      )}>
        <DialogHeader>
          <DialogTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
            {online ? (
              <Cloud className="w-5 h-5 text-emerald-400" />
            ) : (
              <WifiOff className="w-5 h-5 text-amber-400" />
            )}
            Offline Queue
          </DialogTitle>
          <DialogDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
            {online 
              ? 'You\'re online. Forms will submit directly.'
              : 'You\'re offline. Forms will be queued and synced when back online.'}
          </DialogDescription>
        </DialogHeader>

        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-3 py-4">
          <div className={cn(
            "text-center p-3 rounded-lg",
            isDark ? "bg-slate-800/50" : "bg-gray-100"
          )}>
            <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
              {stats.pending}
            </p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Pending</p>
          </div>
          <div className={cn(
            "text-center p-3 rounded-lg",
            isDark ? "bg-slate-800/50" : "bg-gray-100"
          )}>
            <p className={cn("text-2xl font-bold text-emerald-400")}>
              {stats.total - stats.pending - stats.failed}
            </p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Synced</p>
          </div>
          <div className={cn(
            "text-center p-3 rounded-lg",
            isDark ? "bg-slate-800/50" : "bg-gray-100"
          )}>
            <p className={cn("text-2xl font-bold text-red-400")}>
              {stats.failed}
            </p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Failed</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mb-4">
          <Button
            size="sm"
            onClick={handleSync}
            disabled={!online || syncing || stats.pending === 0}
            className="flex-1"
            data-testid="sync-queue-btn"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Sync Now
          </Button>
          {hasFailed && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleRetryFailed}
              disabled={syncing}
              className={isDark ? "border-slate-700" : "border-gray-300"}
              data-testid="retry-failed-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry Failed
            </Button>
          )}
        </div>

        {/* Queue Items */}
        <ScrollArea className="h-[250px]">
          {queueItems.length === 0 ? (
            <div className={cn(
              "text-center py-8",
              isDark ? "text-slate-500" : "text-gray-400"
            )}>
              <Cloud className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No items in queue</p>
            </div>
          ) : (
            <div className="space-y-2">
              {queueItems.map((item) => {
                const StatusIcon = StatusIcons[item.status];
                return (
                  <div
                    key={item.id}
                    className={cn(
                      "p-3 rounded-lg border",
                      isDark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-200"
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center",
                          StatusColors[item.status]
                        )}>
                          <StatusIcon className={cn(
                            "w-4 h-4",
                            item.status === QueueStatus.SYNCING && "animate-spin"
                          )} />
                        </div>
                        <div>
                          <p className={cn("font-medium text-sm", isDark ? "text-white" : "text-gray-900")}>
                            {FormTypeLabels[item.formType] || item.formType}
                          </p>
                          <p className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                            {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                          </p>
                          {item.error && (
                            <p className="text-xs text-red-400 mt-1">{item.error}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveItem(item.id)}
                        className={cn(
                          "h-7 w-7",
                          isDark ? "text-slate-400 hover:text-red-400" : "text-gray-400 hover:text-red-600"
                        )}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Clear Synced */}
        {stats.total - stats.pending - stats.failed > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearSynced}
            className={cn(
              "w-full mt-2",
              isDark ? "text-slate-400 hover:text-slate-300" : "text-gray-500 hover:text-gray-700"
            )}
          >
            Clear Synced Items
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
